import dynamic from 'next/dynamic';
import { sanitizeHTML } from "@/lib/utils";
const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface BlogExploreData {
  heading?: {
    text?: string;
    title?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title?: string;
          text?: string;
          href?: string;
          label?: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // heading
  "heading.text",
  "heading.title",

  // content collection for blog posts
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface BlogExploreProps {  
  data?: BlogExploreData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][]; 
  helperRotation?: number;
}

export default function BlogExplore({ data, zIndex, thresholds, transitionTime, mobileThresholds, helperRotation }: BlogExploreProps) { 
  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32 bg-[#94a4bc]  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: "100vh", x: 0, opacity: 1 },
              below: { y: "-100vh", x: 0, opacity: 1 },
            },
            mobile: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "-100vw", y: 0, opacity: 1 },
              below: { x: 0, y: "-100vh", opacity: 1 },
            }
          }}
        > 
          <div className="h-full overflow-hidden relative w-full flex items-center">

            <AnimatedSection 
              section="div"
            className="relative py-10 lg:py-0 max-h-screen overflow-y-auto w-full" zIndex={zIndex} thresholds={[
                  thresholds?.[0]?.[0],
                  thresholds?.[thresholds.length - 1]?.[1],
                ]} transitionTime={transitionTime}
                variants={{
                  desktop: {
                    visible: { y: 0,  opacity: 1 },
                    above: { y: "100vh",  opacity: 1 },
                    below: { y: 0, opacity: 1 },
                  },
                  mobile: {
                    visible: { y: 0, opacity: 1 },
                    above: { y: "100vh", opacity: 1 },
                    below: { y: 0, opacity: 1 },
                  }
                }}
              >
              <div className="gl-container mx-auto">
              {/* Header */}
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white mb-6 lg:mb-24 lg:text-center max-w-[12ch] lg:max-w-none">
                {data?.heading?.title || "Explore Our Plastic Surgery Blog"}
              </h2>

              {/* Cards Container */}
              <div className="flex flex-col lg:flex-row gap-5 lg:gap-10">
                {data?.content_collection?.map((collection, index) => {
                  const titleItem = collection?.site_content_collections_id?.collection.find(item => item.item.title)?.item;
                  const textItem = collection?.site_content_collections_id?.collection.find(item => item.item.text)?.item;
                  const menuItem = collection?.site_content_collections_id?.collection.find(item => item.item.href)?.item;
                  
                  const title = titleItem?.title || "";
                  const text = textItem?.text || "";
                  const href = menuItem?.href || "#";
                  
                  return (
                    <div key={index} className="flex-1 p-8 bg-white lg:bg-transparent rounded-2xl border border-white flex flex-col gap-4">
                      <h3 className="text-2xl lg:text-4xl font-light font-essonnes text-primary lg:text-white leading-loose lg:leading-10 tracking-tight">
                        {title}
                      </h3>
                      <div 
                        className="hidden lg:block text-xl text-white font-greycliff leading-7 tracking-tight"
                        dangerouslySetInnerHTML={sanitizeHTML(text || "")}
                      />
                      <div className="lg:hidden w-16 h-16 rounded-full border border-primary flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                      <div className="hidden lg:block">
                        <button className="px-6 py-3.5 rounded-3xl border border-white w-fit">
                          <span className="text-xl text-white font-medium font-greycliff leading-7 tracking-tight">
                            {menuItem?.label || "Read more"}
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full py-8 lg:py-16 bg-[#94a4bc]"> 
          <div className="h-full overflow-hidden relative w-full flex items-center">

            <div className="relative xl:py-0 w-full">
              <div className="gl-container mx-auto">
              {/* Header */}
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white mb-6 xl:mb-24 xl:text-center max-w-[12ch] xl:max-w-none">
                {data?.heading?.title || "Explore Our Plastic Surgery Blog"}
              </h2>

              {/* Cards Container */}
              <div className="flex flex-col xl:flex-row gap-5 xl:gap-10">
                {data?.content_collection?.map((collection, index) => {
                  const titleItem = collection?.site_content_collections_id?.collection.find(item => item.item.title)?.item;
                  const textItem = collection?.site_content_collections_id?.collection.find(item => item.item.text)?.item;
                  const menuItem = collection?.site_content_collections_id?.collection.find(item => item.item.href)?.item;
                  
                  const title = titleItem?.title || "";
                  const text = textItem?.text || "";
                  const href = menuItem?.href || "#";
                  
                  return (
                    <div key={index} className="flex-1 p-8 bg-white xl:bg-transparent rounded-2xl border border-white flex flex-col gap-4">
                      <h3 className="text-2xl xl:text-4xl font-light font-essonnes text-primary xl:text-white leading-loose xl:leading-10 tracking-tight">
                        {title}
                      </h3>
                      <div 
                        className="hidden xl:block text-xl text-white font-greycliff leading-7 tracking-tight"
                        dangerouslySetInnerHTML={sanitizeHTML(text || "")}
                      />
                      <div className="xl:hidden w-16 h-16 rounded-full border border-primary flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                      <div className="hidden xl:block">
                        <button className="px-6 py-3.5 rounded-3xl border border-white w-fit">
                          <span className="text-xl text-white font-medium font-greycliff leading-7 tracking-tight">
                            {menuItem?.label || "Read more"}
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}